import React, { useEffect } from 'react';

// The habitat.inc homepage: Conway's Game of Life hero + manifesto.
// Ported verbatim from prototypes/life-grid.html — that file is the design
// reference; keep the two in sync when tuning the simulation.

const CSS = `
  html { scroll-behavior: smooth; }
  html, body {
    margin: 0;
    background: #060608;
    font-family: "Inter", system-ui, -apple-system, sans-serif;
  }
  body { overflow-x: hidden; }

  /* ---- hero ---- */
  #hero { position: relative; height: 100vh; height: 100svh; overflow: hidden; }
  #life {
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    display: block;
    cursor: crosshair;
  }
  .center {
    position: absolute; left: 0; right: 0; top: 44%;
    text-align: center;
    pointer-events: none;
    z-index: 2;
  }
  .center .card {
    text-align: center;
    display: inline-block;
    padding: 3rem 5rem 3.2rem;
    background: radial-gradient(ellipse 58% 54% at center,
      rgba(6,6,8,0.85) 0%,
      rgba(6,6,8,0.55) 46%,
      rgba(6,6,8,0) 72%);
  }
  .center h1 {
    margin: 0;
    color: #f2f2ee;
    font-weight: 600;
    font-size: clamp(28px, 4.5vw, 54px);
    letter-spacing: 0.05em;
    text-indent: 0.05em;
  }
  .center p {
    margin: 14px 0 0;
    color: rgba(242,242,238,0.45);
    font-weight: 400;
    font-size: clamp(12px, 1.2vw, 15px);
    letter-spacing: 0.12em;
    text-indent: 0.12em;
  }

  /* ---- scroll cue ---- */
  .scrolldown {
    position: absolute; bottom: 28px; left: 50%;
    transform: translateX(-50%);
    background: none; border: none; padding: 10px; margin: 0;
    cursor: pointer; z-index: 3;
    animation: cue 2.2s ease-in-out infinite;
  }
  .scrolldown svg { display: block; }
  @keyframes cue {
    0%, 100% { opacity: 0.3; transform: translateX(-50%) translateY(0); }
    50%      { opacity: 0.95; transform: translateX(-50%) translateY(7px); }
  }
  @media (prefers-reduced-motion: reduce) {
    .scrolldown { animation: none; opacity: 0.7; }
  }

  /* ---- manifesto ---- */
  #manifesto { background: #060608; padding: 120px 24px 100px; }
  .m-inner { max-width: 600px; margin: 0 auto; }
  #manifesto p {
    color: rgba(242,242,238,0.78);
    font-size: 17px;
    line-height: 1.75;
    letter-spacing: -0.005em;
    margin: 0 0 26px;
  }
  #manifesto p.lead {
    color: #f2f2ee;
    font-size: clamp(20px, 2.4vw, 26px);
    font-weight: 600;
    line-height: 1.45;
    margin-bottom: 34px;
  }
  #manifesto p.turn { color: #f2f2ee; font-weight: 500; }
  #manifesto a { color: #f2f2ee; text-decoration: underline; text-underline-offset: 3px; }
  #manifesto a:hover { color: #ffffff; }
  .m-footer {
    max-width: 600px; margin: 90px auto 0;
    padding-top: 26px;
    border-top: 1px solid rgba(255,255,255,0.08);
    color: rgba(242,242,238,0.35);
    font-size: 13px;
    letter-spacing: 0.02em;
  }
  /* ---- mobile ---- */
  @media (max-width: 640px) {
    .center .card { padding: 2rem 1.6rem 2.2rem; }
    .center h1 { font-size: clamp(30px, 9vw, 40px); }
    .center p { font-size: 13px; letter-spacing: 0.1em; margin-top: 10px; }
    #manifesto { padding: 84px 22px 72px; }
    #manifesto p { font-size: 16px; }
    #manifesto p.lead { font-size: 22px; }
    .m-footer { margin-top: 64px; font-size: 12px; }
  }

`;

export default function LifeGridHome() {
  useEffect(() => {

  const canvas = document.getElementById('life');
  const ctx = canvas.getContext('2d', { alpha: false });
  const caption = document.querySelector('.center');

  // ---- frozen configuration ----------------------------------------------
  let PITCH = 26, CELL = 20, RADIUS = 5;
  const TICK_MS = 833;             // ~1.2 generations / second
  const PAD_CELLS = 10;            // simulated but invisible skirt: the field is effectively infinite
  // ellipsoidal vignette over the visible rectangle (fade starts ~60% out, ends just past the edge)
  const VIG_START = 0.60, VIG_END = 1.02, VIG_P = 3;
  // ten fresh candidate openings — press 1..9,0 to switch; pick the winner
  const SEED_VALUE = 1250;
  const SEED_DENSITY = 0.14;
  const MIN_POP_FRAC = 0.04;
  const BG = '#060608';
  const C_NEW = [214, 219, 228], C_MID = [166, 172, 183], C_OLD = [99, 105, 116];
  const DEAD_ALPHA = 0.054;
  const FLASH_MS = 240, FLASH_AMT = 0.5;
  const CLICK_R = 1;               // click spawns a plus sign (no diagonals)
  // ------------------------------------------------------------------------

  let W, H, cols, rows, visCols, visRows, ox, oy;
  let grid, next, age, glow, fade;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // deterministic RNG so every visitor sees the same opening
  let rngState = 1;
  function srand(v) { rngState = v >>> 0; }
  function rnd() {
    rngState = rngState + 0x6D2B79F5 | 0;
    let t = rngState;
    t = Math.imul(t ^ (t >>> 15), 1 | t);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  const idx = (x, y) => y * cols + x;
  const inBounds = (x, y) => x >= 0 && x < cols && y >= 0 && y < rows;

  // keep the opening soup and reseeds away from the caption
  let excl = null;
  function buildExclusion() {
    excl = null;
    const card = caption ? caption.querySelector('.card') : null;
    if (!card) return;
    const r = card.getBoundingClientRect(), m = 20;
    const heroTop = canvas.getBoundingClientRect().top;
    excl = {
      x0: Math.floor((r.left - m - ox) / PITCH), x1: Math.floor((r.right + m - ox) / PITCH),
      y0: Math.floor((r.top - heroTop - m - oy) / PITCH), y1: Math.floor((r.bottom - heroTop + m - oy) / PITCH),
    };
  }
  const inExcl = (x, y) => excl && x >= excl.x0 && x <= excl.x1 && y >= excl.y0 && y <= excl.y1;

  function place() {
    const card = caption ? caption.querySelector('.card') : null;
    const capH = Math.min(card ? card.offsetHeight : 110, 320);
    const visW = visCols * PITCH, visH = visRows * PITCH;
    const rectLeft = (W - visW) / 2;
    const rectTop = Math.max(28, (H - visH) * 0.47);
    ox = rectLeft - PAD_CELLS * PITCH;
    oy = rectTop - PAD_CELLS * PITCH;
    if (caption) caption.style.top = Math.round(rectTop + visH / 2 - capH / 2) + 'px';
  }

  function resize() {
    const dpr = Math.min(devicePixelRatio || 1, 2);
    W = canvas.clientWidth; H = canvas.clientHeight;
    const mobile = W < 640;
    PITCH = mobile ? 20 : 26; CELL = mobile ? 15 : 20; RADIUS = mobile ? 4 : 5;
    canvas.width = W * dpr; canvas.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    visCols = Math.floor((W * (mobile ? 0.88 : 0.625)) / PITCH);
    visRows = Math.floor((H * (mobile ? 0.60 : 0.625)) / PITCH);
    cols = visCols + 2 * PAD_CELLS;
    rows = visRows + 2 * PAD_CELLS;
    place();
    const n = cols * rows;
    grid = new Uint8Array(n);
    next = new Uint8Array(n);
    age = new Uint16Array(n);
    glow = new Float32Array(n);
    fade = new Float32Array(n);
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const nx = Math.abs(x - (PAD_CELLS + (visCols - 1) / 2)) / (visCols / 2);
        const ny = Math.abs(y - (PAD_CELLS + (visRows - 1) / 2)) / (visRows / 2);
        const r = Math.pow(Math.pow(nx, VIG_P) + Math.pow(ny, VIG_P), 1 / VIG_P);
        const t = Math.max(0, Math.min(1, (VIG_END - r) / (VIG_END - VIG_START)));
        fade[y * cols + x] = t * t * (3 - 2 * t);
      }
    }
    seed();
  }

  const GLIDER = [[1,0],[2,1],[0,2],[1,2],[2,2]];
  const RPENT = [[1,0],[2,0],[0,1],[1,1],[1,2]];
  const ACORN = [[1,0],[3,1],[0,2],[1,2],[4,2],[5,2],[6,2]];
  const LWSS = [[1,0],[4,0],[0,1],[0,2],[4,2],[0,3],[1,3],[2,3],[3,3]];
  const PATTERNS = [GLIDER, GLIDER, RPENT, LWSS, ACORN];

  function stamp(pattern, cx, cy, flip) {
    for (const [px, py] of pattern) {
      const x = cx + (flip & 1 ? -px : px);
      const y = cy + (flip & 2 ? -py : py);
      if (inBounds(x, y)) { grid[idx(x, y)] = 1; age[idx(x, y)] = 0; }
    }
  }

  function seed() {
    srand(SEED_VALUE);
    buildExclusion();
    const x0 = Math.floor(cols * 0.12), x1 = Math.floor(cols * 0.88);
    const y0 = Math.floor(rows * 0.12), y1 = Math.floor(rows * 0.88);
    for (let y = y0; y < y1; y++)
      for (let x = x0; x < x1; x++)
        if (rnd() < SEED_DENSITY && !inExcl(x, y)) grid[idx(x, y)] = 1;
    for (let i = 0; i < 4; i++) {
      let gx, gy, tries = 0;
      do {
        gx = 4 + Math.floor(rnd() * (cols - 8));
        gy = 4 + Math.floor(rnd() * (rows - 8));
      } while (inExcl(gx, gy) && ++tries < 24);
      stamp(GLIDER, gx, gy, Math.floor(rnd() * 4));
    }
  }

  let stagnantTicks = 0;
  function stampRim(cells) {
    const a = Math.random() * 2 * Math.PI, rf = 0.55 + Math.random() * 0.3;
    const cx = (cols - 1) / 2, cy = (rows - 1) / 2;
    const px = cx + Math.cos(a) * (cx - 2) * rf, py = cy + Math.sin(a) * (cy - 2) * rf;
    stamp(cells, Math.round(px), Math.round(py), Math.floor(Math.random() * 4));
  }

  function step() {
    let pop = 0, changed = 0;
    for (let y = 0; y < rows; y++) {
      const up = ((y - 1) + rows) % rows, dn = (y + 1) % rows;
      for (let x = 0; x < cols; x++) {
        const lf = ((x - 1) + cols) % cols, rt = (x + 1) % cols;
        const n =
          grid[idx(lf, up)] + grid[idx(x, up)] + grid[idx(rt, up)] +
          grid[idx(lf, y)] + grid[idx(rt, y)] +
          grid[idx(lf, dn)] + grid[idx(x, dn)] + grid[idx(rt, dn)];
        const i = idx(x, y);
        const alive = grid[i] ? (n === 2 || n === 3) : (n === 3);
        next[i] = alive ? 1 : 0;
        if (next[i] !== grid[i]) changed++;
        age[i] = alive ? (grid[i] ? Math.min(age[i] + 1, 600) : 0) : 0;
        pop += next[i];
      }
    }
    [grid, next] = [next, grid];
    // never let the field fossilize: stir a settled board with a methuselah
    if (changed < 6) {
      if (++stagnantTicks > 18) { stampRim(RPENT); stagnantTicks = 0; }
    } else stagnantTicks = 0;
    // never let it thin out either
    if (pop < cols * rows * MIN_POP_FRAC) {
      const p = PATTERNS[Math.floor(Math.random() * PATTERNS.length)];
      let wx, wy, tries = 0;
      do {
        wx = 6 + Math.floor(Math.random() * (cols - 12));
        wy = 6 + Math.floor(Math.random() * (rows - 12));
      } while (inExcl(wx, wy) && ++tries < 24);
      stamp(p, wx, wy, Math.floor(Math.random() * 4));
    }
  }

  function cellColor(a) {
    let c;
    if (a <= 8) {
      const t = a / 8;
      c = C_NEW.map((v, i) => v + (C_MID[i] - v) * t);
    } else {
      const t = Math.min(1, (a - 8) / 52);
      c = C_MID.map((v, i) => v + (C_OLD[i] - v) * t);
    }
    return c;
  }

  function draw(now) {
    const flashF = reduced ? 0 : Math.max(0, 1 - (now - last) / FLASH_MS);
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, W, H);
    const off = (PITCH - CELL) / 2;
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const i = idx(x, y);
        const f = fade[i];
        if (f <= 0.01) continue;
        const target = grid[i] ? 1 : 0;
        glow[i] += (target - glow[i]) * (reduced ? 1 : 0.16);
        const g = glow[i];
        const px = ox + x * PITCH + off, py = oy + y * PITCH + off;
        if (g < 0.02) {
          ctx.globalAlpha = DEAD_ALPHA * f;
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.roundRect(px, py, CELL, CELL, RADIUS);
          ctx.fill();
        } else {
          let [r, gc, b] = cellColor(age[i]);
          if (flashF > 0 && grid[i]) { // generation pulse
            const k = flashF * FLASH_AMT;
            r += (238 - r) * k; gc += (242 - gc) * k; b += (248 - b) * k;
          }
          ctx.globalAlpha = 0.06 * g * f;
          ctx.fillStyle = `rgb(${r|0},${gc|0},${b|0})`;
          ctx.beginPath();
          ctx.roundRect(px - 4, py - 4, CELL + 8, CELL + 8, RADIUS + 4);
          ctx.fill();
          ctx.globalAlpha = Math.min(1, 0.12 + 0.73 * g) * f;
          ctx.beginPath();
          ctx.roundRect(px, py, CELL, CELL, RADIUS);
          ctx.fill();
        }
      }
    }
    ctx.globalAlpha = 1;
  }

  // click spawns a plus sign that joins the simulation
  canvas.addEventListener('pointerdown', e => {
    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left - ox) / PITCH);
    const y = Math.floor((e.clientY - rect.top - oy) / PITCH);
    if (!inBounds(x, y)) return;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      if (Math.abs(dx) + Math.abs(dy) > CLICK_R) continue;
      const nx = x + dx, ny = y + dy;
      if (inBounds(nx, ny)) {
        const j = idx(nx, ny);
        grid[j] = 1; age[j] = 0; glow[j] = Math.max(glow[j], 0.5);
      }
    }
  });


  let last = 0, rafId = 0;
  function loop(t) {
    if (!reduced && t - last >= TICK_MS) { last = t; step(); }
    draw(t);
    rafId = requestAnimationFrame(loop);
  }

  window.addEventListener('resize', resize);
  const onLoad = () => { place(); buildExclusion(); };
  window.addEventListener('load', onLoad);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { place(); buildExclusion(); });
  resize();
  if (reduced) step();
  rafId = requestAnimationFrame(loop);

  return () => {
    cancelAnimationFrame(rafId);
    window.removeEventListener('resize', resize);
    window.removeEventListener('load', onLoad);
  };
  }, []);

  // dark address bar on mobile while this page is mounted
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    const prev = meta ? meta.getAttribute('content') : null;
    if (meta) meta.setAttribute('content', '#060608');
    return () => { if (meta && prev) meta.setAttribute('content', prev); };
  }, []);

  return (
    <div className="lifegrid">
      <style>{CSS}</style>
      <section id="hero">
        <canvas id="life"></canvas>
        <div className="center">
          <div className="card">
            <h1>HABITAT</h1>
            <p>the autonomous firm</p>
          </div>
        </div>
        <button className="scrolldown" aria-label="Scroll to read more"
          onClick={() => document.getElementById('manifesto').scrollIntoView({ behavior: 'smooth' })}>
          <svg width="26" height="16" viewBox="0 0 26 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 2 L13 13 L24 2" stroke="#f2f2ee" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </section>

      <section id="manifesto">
        <div className="m-inner">
          <p className="lead">The economy is transitioning from human to machine intelligence.</p>
          <p>It is one of the greatest opportunities for human abundance and flourishing, but at the same time, one of the greatest risks.</p>
          <p>Across many pockets of the economy, autonomous agent-run firms will outperform human ones. They will optimize their funnels better. They will better anticipate the human intent behind their products. They will build more delightful experiences.</p>
          <p>The economy is a global allocator of power and capital. That’s why it’s so critical to have clear visibility into the actions of autonomous firms.</p>
          <p className="turn">So Habitat is building the first autonomous firm.</p>
          <p>We buy post-PMF businesses and deploy machine intelligence across our portfolio, using a combination of closed and open-source models. Our portfolio enables us to study how machine intelligence actually behaves in the real economy, particularly when money is on the line. We monitor for misalignment and intervene when agent actions diverge from human interests. We aim to disclose important safety incidents and identify misalignment before it happens at scale.</p>
          <p>If you’re excited about understanding the agent economy, have experience taking products to PMF and beyond, or want to build evals to optimally run a business — <a href="mailto:maxim@habitat.inc">let’s chat</a>.</p>
        </div>
        <div className="m-footer">
          <a href="https://www.linkedin.com/company/habitat-ai-inc" target="_blank" rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'none' }}>
            <img src="/habitat-badge.svg" alt="" style={{ width: 18, height: 18, verticalAlign: -4, marginRight: 6 }} />
            Habitat Inc.
          </a> · Backed by General Catalyst · <a href="mailto:maxim@habitat.inc" style={{ color: 'inherit' }}>maxim@habitat.inc</a>
        </div>
      </section>
    </div>
  );
}
